// @givmo/crm — the shared contacts screen.
//
// THREE TABS OVER TWO RECORD MODES, NEVER MERGED (CRM-5):
//
//   • ALL — the organization's whole list from the server's combined read, ordered by name and
//     paged there: Givmo rows and the organization's own rows side by side, each saying where it
//     came from in a quiet Source column. Two rows that share an address stay two rows.
//   • GIVMO (FEED) — identity resolves only through the server's live consent predicate. A closed
//     predicate returns `consent_available: false` plus server-authored copy, rendered verbatim.
//   • IMPORTED (SUPPLIED) — records the organization provided. They carry no Givmo subject reference
//     and are never matched, joined, deduped, or combined with feed identity. The profile words for
//     both modes enter through `CrmModuleVocab`; this engine never learns an entity class.
//
// A contact the organization may not email (do-not-contact, or its address on the suppression list)
// says so after its address in a plain word, on every tab.
//
// CPB-WEB8: where the host mounts the import, All asks for one thing until the organization has
// contacts of its own: "Bring your contacts" alone in its toolbar. After that All keeps its usual
// actions, and the import stays on the Imported tab, where its rows land.
//
// D-CP-32: tag and note writes are offered for EVERY listed feed subject (on its record page) and
// special-cased for none. Any residual qualification refusal stays uniform so the write path cannot
// be used as a subject oracle.
//
// NOT A RECEIPT: no receipt/deductibility framing exists in this feature.

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  EmptyState,
  Icon,
  Menu,
  PageHeader,
  SearchInput,
  Skeleton,
  Tabs,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ChevronDown, Download, Plus, ShieldAlert, Upload, Users } from 'lucide-react';
import type { CrmModuleConfig } from './crm-config';
import type { CrmContactOut, CrmDonorOut } from './wire-types';
import {
  crmContactsOptions,
  crmDonorsOptions,
  crmImportedContactsOptions,
  crmKeys,
} from './crm-queries';
import { exportCrmDonorsCsv, exportCrmImportedContactsCsv } from './crm-transport';
import { fallbackCsvName, runCsvDownload } from './csv-download';
import { useDebouncedValue } from './use-debounced-value';
import { contactLastGift, displayText, isSliceUnavailable, serverMessage } from './crm-decode';
import { ContactsPagination, ContactsTable, type ContactsTableRow } from './contacts-table';
import { ImportedContactDialog } from './imported-contact-dialog';
import { ContactSheet } from './contact-sheet';
import { BringContactsButton } from './crm-import-wizard';

const PREFIX = 'crm';

/**
 * Rows per page on every tab: the approved screen's ten, so the table and its pager fit one 1440×900
 * view. The backend rejects anything above 200 rather than clamping.
 */
export const CONTACTS_PAGE_SIZE = 10;

type ContactsTab = 'all' | 'donors' | 'imported';

export function CrmContactsPage({
  config,
  initialContactId,
}: {
  config: CrmModuleConfig;
  /** Open on one of the organization's own contacts (a link to it, or the preview harness). */
  initialContactId?: string;
}) {
  const [tab, setTab] = useState<ContactsTab>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openContactId, setOpenContactId] = useState<string | null>(initialContactId ?? null);
  const queryClient = useQueryClient();
  // A saved, marked or removed contact changes every list it can appear on (and every segment count).
  const refreshLists = () =>
    void queryClient.invalidateQueries({ queryKey: crmKeys.org(config.orgRef) });

  const openCreate = () => setDialogOpen(true);

  return (
    <PageShell config={config}>
      <Tabs.Root
        appearance="saas"
        value={tab}
        onValueChange={(value) => setTab(value as ContactsTab)}
      >
        <Tabs.List aria-label="Contact source">
          <Tabs.Tab value="all" testId="tab-all">
            {config.vocab.allTabLabel}
          </Tabs.Tab>
          <Tabs.Tab value="donors" testId="tab-donors">
            {config.vocab.feedTabLabel}
          </Tabs.Tab>
          <Tabs.Tab value="imported" testId="tab-imported">
            {config.vocab.suppliedTabLabel}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all">
          <AllTab config={config} onAdd={openCreate} onOpenContact={setOpenContactId} />
        </Tabs.Panel>

        <Tabs.Panel value="donors">
          <DonorsTab config={config} />
        </Tabs.Panel>

        <Tabs.Panel value="imported">
          <ImportedTab config={config} onAdd={openCreate} onOpenContact={setOpenContactId} />
        </Tabs.Panel>
      </Tabs.Root>

      <ImportedContactDialog
        config={config}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refreshLists}
      />
      <ContactSheet
        config={config}
        contactId={openContactId}
        onClose={() => setOpenContactId(null)}
        onChanged={refreshLists}
      />
    </PageShell>
  );
}

// ── The toolbar every tab shares ──────────────────────────────────────────────────────────────────

function Toolbar({
  search,
  onSearch,
  searchTestId,
  children,
}: {
  search: string;
  onSearch: (value: string) => void;
  searchTestId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-12">
      <SearchInput
        placeholder="Search name or email"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        onClear={() => onSearch('')}
        containerClassName="w-full max-w-[320px]"
        testId={searchTestId}
      />
      <div className="flex items-center gap-8">{children}</div>
    </div>
  );
}

function AddContactButton({
  onAdd,
  testId,
  tone = 'primary',
}: {
  onAdd: () => void;
  testId: string;
  tone?: 'primary' | 'secondary';
}) {
  return (
    <Button
      appearance="saas"
      tone={tone}
      size="sm"
      leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
      onClick={onAdd}
      testId={testId}
    >
      Add contact
    </Button>
  );
}

function useCsvDownload() {
  const [downloading, setDownloading] = useState(false);
  const download = async (
    run: () => ReturnType<typeof exportCrmDonorsCsv>,
    fallback: Parameters<typeof fallbackCsvName>[0],
    done: string,
  ) => {
    setDownloading(true);
    try {
      await runCsvDownload(run, fallbackCsvName(fallback));
      toast.success(done);
    } catch (downloadError) {
      toast.error(
        serverMessage(downloadError, 'Could not download your contacts. Please try again.'),
      );
    } finally {
      setDownloading(false);
    }
  };
  return { downloading, download };
}

function LoadingRows({ testId }: { testId: string }) {
  return (
    <div className="flex flex-col gap-10" {...testIdProp(testId)}>
      <Skeleton className="h-12 w-1/3" />
      <Skeleton className="h-12 w-2/3" />
    </div>
  );
}

function LoadError({
  testId,
  error,
  onRetry,
  retryTestId,
}: {
  testId: string;
  error: unknown;
  onRetry: () => void;
  retryTestId: string;
}) {
  return (
    <EmptyState
      testId={testId}
      icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
      title="Couldn’t load your contacts"
      description={serverMessage(error, 'We couldn’t load your contacts. Please try again.')}
      action={
        <Button appearance="saas" tone="secondary" size="sm" onClick={onRetry} testId={retryTestId}>
          Try again
        </Button>
      }
    />
  );
}

function sourceWord(config: CrmModuleConfig, source: string): string {
  return source === 'feed' ? config.vocab.feedSourceLabel : config.vocab.suppliedSourceLabel;
}

function DonorName({
  config,
  donorId,
  name,
}: {
  config: CrmModuleConfig;
  donorId: string;
  name: string | null | undefined;
}) {
  return (
    <Link
      to={config.links.donor(donorId)}
      className="rounded-sm font-w600 text-text-strong outline-none hover:underline focus-visible:shadow-ring"
      {...testIdProp(`${PREFIX}-donor-name`)}
    >
      {displayText(name, config.vocab.anonymousFeedRecordLabel)}
    </Link>
  );
}

/** One of the organization's own contacts: its name opens its panel. */
function ContactName({
  contactId,
  name,
  email,
  onOpen,
}: {
  contactId: string;
  name: string | null | undefined;
  email: string | null | undefined;
  onOpen: (contactId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(contactId)}
      className="rounded-sm text-left font-w600 text-text-strong outline-none hover:underline focus-visible:shadow-ring"
      {...testIdProp(`${PREFIX}-contact-open-${contactId}`)}
    >
      {displayText(name, displayText(email))}
    </button>
  );
}

// ── All ───────────────────────────────────────────────────────────────────────────────────────────

function AllTab({
  config,
  onAdd,
  onOpenContact,
}: {
  config: CrmModuleConfig;
  onAdd: () => void;
  onOpenContact: (contactId: string) => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const { downloading, download } = useCsvDownload();

  const contactsQuery = useQuery(
    crmContactsOptions(config.orgRef, {
      q: debouncedSearch,
      limit: CONTACTS_PAGE_SIZE,
      offset,
    }),
  );
  const refetch = contactsQuery.refetch;

  const body = contactsQuery.data;
  const contacts = body?.contacts ?? [];
  const total = body?.total ?? 0;

  // CPB-WEB8: whether the organization has contacts of its own yet, from the Imported tab's first
  // page (the same cache). Until it answers, the toolbar holds no actions rather than the wrong ones.
  const importOffered = config.links.import !== undefined;
  const ownContactsQuery = useQuery({
    ...crmImportedContactsOptions(config.orgRef, { q: '', limit: CONTACTS_PAGE_SIZE, offset: 0 }),
    enabled: importOffered,
  });
  const actionsPending = importOffered && ownContactsQuery.isPending;
  const firstRun = importOffered && ownContactsQuery.data?.total === 0;

  const onSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
  };

  const rows: ContactsTableRow[] = contacts.map((contact: CrmContactOut) => {
    const donorId = contact.source === 'feed' ? contact.donor_id : null;
    return {
      key: contact.contact_id,
      testId: `${PREFIX}-contact-row-${contact.contact_id}`,
      name: donorId ? (
        <DonorName config={config} donorId={donorId} name={contact.name} />
      ) : (
        <ContactName
          contactId={contact.contact_id}
          name={contact.name}
          email={contact.email}
          onOpen={onOpenContact}
        />
      ),
      // The whole row opens the record too: a Givmo row its donor record, the organization's own its
      // panel. (Keyboard reaches the same place through the name.)
      onOpen: donorId
        ? () => void navigate({ to: config.links.donor(donorId) })
        : () => onOpenContact(contact.contact_id),
      email: contact.email,
      doNotEmail: contact.do_not_email === true,
      tags: contact.tags ?? [],
      lastGift: contactLastGift(contact),
      source: sourceWord(config, contact.source),
    };
  });

  return (
    <div className="flex flex-col gap-16">
      <Toolbar search={search} onSearch={onSearch} searchTestId={`${PREFIX}-contacts-search`}>
        {actionsPending ? null : firstRun ? (
          <BringContactsButton config={config} size="sm" />
        ) : (
          <>
            <Menu.Root>
              <Menu.Trigger
                disabled={downloading}
                render={
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                    trailingIcon={<Icon icon={ChevronDown} size={14} aria-hidden />}
                    testId={`${PREFIX}-contacts-csv`}
                  />
                }
              >
                Download CSV
              </Menu.Trigger>
              <Menu.Content saas align="end">
                {/* Closed consent: the Givmo file would hold its header alone, so it is not
                    offered, as the Givmo tab's own button is not. */}
                <Menu.Item
                  saas
                  disabled={body?.consent_available !== true}
                  onClick={() =>
                    void download(
                      () => exportCrmDonorsCsv(config.orgRef),
                      'crm-donors',
                      'Contact list downloaded',
                    )
                  }
                  testId={`${PREFIX}-contacts-csv-feed`}
                >
                  {config.vocab.feedTabLabel}
                </Menu.Item>
                <Menu.Item
                  saas
                  onClick={() =>
                    void download(
                      () => exportCrmImportedContactsCsv(config.orgRef),
                      'imported-contacts',
                      'Supplied contacts downloaded',
                    )
                  }
                  testId={`${PREFIX}-contacts-csv-supplied`}
                >
                  {config.vocab.suppliedTabLabel}
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
            {config.canEdit === false ? null : (
              <AddContactButton onAdd={onAdd} testId={`${PREFIX}-contacts-add`} />
            )}
          </>
        )}
      </Toolbar>

      {body !== undefined && !body.consent_available && body.message ? (
        <p
          className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-contacts-consent-note`)}
        >
          {body.message}
        </p>
      ) : null}

      {contactsQuery.isPending ? (
        <LoadingRows testId={`${PREFIX}-contacts-loading`} />
      ) : contactsQuery.isError && isSliceUnavailable(contactsQuery.error) ? (
        <SliceUnavailable scope="contacts" />
      ) : contactsQuery.isError ? (
        <LoadError
          testId={`${PREFIX}-contacts-error`}
          error={contactsQuery.error}
          onRetry={() => void refetch()}
          retryTestId={`${PREFIX}-contacts-retry`}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          testId={`${PREFIX}-contacts-empty`}
          icon={<Icon icon={Users} size={20} aria-hidden />}
          title={debouncedSearch.length > 0 ? 'No contacts match that search' : 'No contacts yet'}
          description={
            debouncedSearch.length > 0
              ? 'Try a different name or email.'
              : config.vocab.suppliedEmptyDescription
          }
        />
      ) : (
        <ContactsTable
          rows={rows}
          lastGiftLabel={config.vocab.lastActivityLabel}
          showSource
          listTestId={`${PREFIX}-contacts-list`}
          footer={
            total > CONTACTS_PAGE_SIZE ? (
              <ContactsPagination
                offset={offset}
                shown={rows.length}
                total={total}
                busy={contactsQuery.isFetching}
                onPrevious={() => setOffset((value) => Math.max(0, value - CONTACTS_PAGE_SIZE))}
                onNext={() => setOffset((value) => value + CONTACTS_PAGE_SIZE)}
                testId={`${PREFIX}-contacts-pagination`}
              />
            ) : undefined
          }
        />
      )}
    </div>
  );
}

// ── Givmo ─────────────────────────────────────────────────────────────────────────────────────────

function DonorsTab({ config }: { config: CrmModuleConfig }) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const { downloading, download } = useCsvDownload();

  const donorsQuery = useQuery(crmDonorsOptions(config.orgRef));

  // `CrmDonorListOut` is a NAMED wrapper the envelope middleware does not unwrap — read `donors`
  // explicitly. A bare `extractList(body)` would silently return [] forever.
  const consentAvailable = donorsQuery.data?.consent_available;
  const canDownload = donorsQuery.isSuccess && consentAvailable === true && !downloading;
  const needle = search.trim().toLocaleLowerCase();
  const donors = useMemo(() => {
    const all = donorsQuery.data?.donors ?? [];
    if (needle.length === 0) return all;
    return all.filter(
      (donor) =>
        (donor.name ?? '').toLocaleLowerCase().includes(needle) ||
        (donor.email ?? '').toLocaleLowerCase().includes(needle),
    );
  }, [donorsQuery.data, needle]);
  const page = donors.slice(offset, offset + CONTACTS_PAGE_SIZE);

  const onSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
  };

  return (
    <div className="flex flex-col gap-16">
      <Toolbar search={search} onSearch={onSearch} searchTestId={`${PREFIX}-donors-search`}>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
          loading={downloading}
          disabled={!canDownload}
          onClick={() =>
            void download(
              () => exportCrmDonorsCsv(config.orgRef),
              'crm-donors',
              'Contact list downloaded',
            )
          }
          testId="crm-donors-csv"
        >
          Download CSV
        </Button>
      </Toolbar>

      <DonorsBody
        donors={page}
        allCount={donors.length}
        offset={offset}
        onPage={setOffset}
        searching={needle.length > 0}
        consentAvailable={consentAvailable}
        message={donorsQuery.data?.message ?? null}
        isPending={donorsQuery.isPending}
        isError={donorsQuery.isError}
        error={donorsQuery.error}
        config={config}
        onRetry={() => void donorsQuery.refetch()}
      />
    </div>
  );
}

function DonorsBody({
  donors,
  allCount,
  offset,
  onPage,
  searching,
  consentAvailable,
  message,
  isPending,
  isError,
  error,
  config,
  onRetry,
}: {
  donors: CrmDonorOut[];
  allCount: number;
  offset: number;
  onPage: (offset: number) => void;
  searching: boolean;
  consentAvailable: boolean | undefined;
  message: string | null;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  config: CrmModuleConfig;
  onRetry: () => void;
}) {
  const navigate = useNavigate();
  if (isPending) return <LoadingRows testId={`${PREFIX}-donors-loading`} />;

  // The whole slice 404s while the backend flag is dark. That is a not-yet, never an error.
  if (isError && isSliceUnavailable(error)) return <SliceUnavailable scope="donors" />;

  if (isError) {
    return (
      <LoadError
        testId={`${PREFIX}-donors-error`}
        error={error}
        onRetry={onRetry}
        retryTestId={`${PREFIX}-donors-retry`}
      />
    );
  }

  // Gate closed: render the SERVER's message. No local copy-fork — the backend owns this sentence.
  if (consentAvailable === false) {
    return (
      <EmptyState
        testId={`${PREFIX}-donors-consent-unavailable`}
        icon={<Icon icon={Users} size={20} aria-hidden />}
        title={config.vocab.consentUnavailableTitle}
        description={message ?? 'Required terms must be accepted before details can be shared.'}
      />
    );
  }

  if (donors.length === 0) {
    return (
      <EmptyState
        testId={`${PREFIX}-donors-empty`}
        icon={<Icon icon={Users} size={20} aria-hidden />}
        title={searching ? 'No contacts match that search' : config.vocab.emptyFeedTitle}
        description={
          searching
            ? 'Try a different name or email.'
            : (message ?? config.vocab.emptyFeedDescription)
        }
      />
    );
  }

  const rows: ContactsTableRow[] = donors.map((donor) => ({
    key: donor.donor_id,
    testId: `${PREFIX}-donor-row-${donor.donor_id}`,
    name: <DonorName config={config} donorId={donor.donor_id} name={donor.name} />,
    onOpen: () => void navigate({ to: config.links.donor(donor.donor_id) }),
    email: donor.email,
    doNotEmail: donor.do_not_email === true,
    tags: donor.tags ?? [],
    lastGift: donor.last_donation_date,
  }));

  return (
    <ContactsTable
      rows={rows}
      lastGiftLabel={config.vocab.lastActivityLabel}
      showSource={false}
      listTestId={`${PREFIX}-donors-list`}
      footer={
        allCount > CONTACTS_PAGE_SIZE ? (
          <ContactsPagination
            offset={offset}
            shown={rows.length}
            total={allCount}
            busy={false}
            onPrevious={() => onPage(Math.max(0, offset - CONTACTS_PAGE_SIZE))}
            onNext={() => onPage(offset + CONTACTS_PAGE_SIZE)}
            testId={`${PREFIX}-donors-pagination`}
          />
        ) : undefined
      }
    />
  );
}

// ── Imported ──────────────────────────────────────────────────────────────────────────────────────

function ImportedTab({
  config,
  onAdd,
  onOpenContact,
}: {
  config: CrmModuleConfig;
  onAdd: () => void;
  onOpenContact: (contactId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const { downloading, download } = useCsvDownload();

  const contactsQuery = useQuery(
    crmImportedContactsOptions(config.orgRef, {
      q: debouncedSearch,
      limit: CONTACTS_PAGE_SIZE,
      offset,
    }),
  );
  const refetch = contactsQuery.refetch;

  // `CrmImportedContactListOut` is a named wrapper too — read `contacts`, and take limit/offset/total
  // off the wrapper itself (they ARE reachable here; the middleware never unwrapped it).
  const contacts = contactsQuery.data?.contacts ?? [];
  const total = contactsQuery.data?.total ?? 0;
  // The empty list asks for the import itself, so the toolbar doesn't ask twice.
  const emptyAsks =
    contactsQuery.isSuccess && contacts.length === 0 && debouncedSearch.length === 0;

  const onSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
  };

  return (
    <div className="flex flex-col gap-16">
      {/* The segregation promise, stated where the records are — persistent, never dismissible. */}
      <div
        className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-16"
        {...testIdProp('imported-segregation-note')}
      >
        <Icon icon={Upload} size={18} className="mt-2 shrink-0 text-icon" aria-hidden />
        <p className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          {config.vocab.suppliedSegregationNote}
        </p>
      </div>

      <Toolbar search={search} onSearch={onSearch} searchTestId={`${PREFIX}-imported-search`}>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
          loading={downloading}
          disabled={downloading}
          onClick={() =>
            void download(
              () => exportCrmImportedContactsCsv(config.orgRef),
              'imported-contacts',
              'Supplied contacts downloaded',
            )
          }
          testId="imported-contacts-csv"
        >
          Download CSV
        </Button>
        {config.canEdit === false ? null : (
          <AddContactButton
            onAdd={onAdd}
            testId={`${PREFIX}-imported-add`}
            tone={config.links.import === undefined ? 'primary' : 'secondary'}
          />
        )}
        {emptyAsks ? null : <BringContactsButton config={config} size="sm" />}
      </Toolbar>

      {contactsQuery.isPending ? (
        <LoadingRows testId={`${PREFIX}-imported-loading`} />
      ) : contactsQuery.isError && isSliceUnavailable(contactsQuery.error) ? (
        <SliceUnavailable scope="imported" />
      ) : contactsQuery.isError ? (
        <LoadError
          testId={`${PREFIX}-imported-error`}
          error={contactsQuery.error}
          onRetry={() => void refetch()}
          retryTestId={`${PREFIX}-imported-retry`}
        />
      ) : contacts.length === 0 ? (
        <EmptyState
          testId={`${PREFIX}-imported-empty`}
          icon={<Icon icon={Upload} size={20} aria-hidden />}
          title={
            debouncedSearch.length > 0
              ? 'No contacts match that search'
              : config.vocab.suppliedEmptyTitle
          }
          description={
            debouncedSearch.length > 0
              ? 'Try a different name or email. Search only looks at your own contacts.'
              : config.vocab.suppliedEmptyDescription
          }
          action={
            debouncedSearch.length > 0 ? undefined : (
              <BringContactsButton config={config} size="sm" />
            )
          }
        />
      ) : (
        <ContactsTable
          rows={contacts.map((contact) => ({
            key: contact.id,
            testId: `${PREFIX}-imported-row-${contact.id}`,
            name: (
              <ContactName
                contactId={contact.id}
                name={contact.display_name}
                email={contact.email}
                onOpen={onOpenContact}
              />
            ),
            onOpen: () => onOpenContact(contact.id),
            email: contact.email,
            doNotEmail: contact.do_not_email === true,
            tags: contact.tags ?? [],
            lastGift: contact.last_gift_date,
          }))}
          lastGiftLabel={config.vocab.lastActivityLabel}
          showSource={false}
          listTestId={`${PREFIX}-imported-list`}
          footer={
            total > CONTACTS_PAGE_SIZE ? (
              <ContactsPagination
                offset={offset}
                shown={contacts.length}
                total={total}
                busy={contactsQuery.isFetching}
                onPrevious={() => setOffset((value) => Math.max(0, value - CONTACTS_PAGE_SIZE))}
                onNext={() => setOffset((value) => value + CONTACTS_PAGE_SIZE)}
                testId={`${PREFIX}-imported-pagination`}
              />
            ) : undefined
          }
        />
      )}
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────────────────────────

/**
 * The backend 404s every CRM route while the module is dark, so a not-yet-enabled environment is
 * indistinguishable from a route that never existed.
 */
function SliceUnavailable({ scope }: { scope: 'contacts' | 'donors' | 'imported' }) {
  return (
    <EmptyState
      testId={`${PREFIX}-${scope}-unavailable`}
      icon={<Icon icon={Users} size={20} aria-hidden />}
      title="Not available yet"
      description="This part of the portal isn’t switched on for your organization yet."
    />
  );
}

function PageShell({ children, config }: { children: React.ReactNode; config: CrmModuleConfig }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-contacts')}
    >
      <PageHeader
        title={config.vocab.contactsTitle}
        subtitle={config.vocab.contactsSubtitle}
        organizationName={config.orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
      />
      {children}
    </div>
  );
}
